import { WelcomeTransitionProvider } from './welcome-transition';
import { KeyedByRecord } from './route-key';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Link,
  Navigate,
  Route,
  Routes,
  matchPath,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { AppShell, CommandDialog, type NavItem, type NavNotice } from '@forecastify/ui';
import { SETTINGS_SECTIONS, SettingsDenied, sectionPath } from './screens/settings/settings-shell';
import { Framed } from './screens/settings/settings-route';
import { ACCOUNT_NAV, type AccountSectionId } from './screens/account/account-screen';
import { ACCOUNT_HEADINGS, WORKSPACE_HEADINGS } from './screens/settings/settings-search-index';
import { ScreenState } from './screens/screen-state';
import { useAccountDirty } from './screens/account/account-dirty';
import { usePermissionSet } from './screens/permissions-hook';
import { avatarSeed } from './screens/identity';
import type { SidebarView } from '@forecastify/shared';
import { I18nProvider, preferredLocale, useT, type Locale } from './i18n';
import {
  HOME_PATH,
  ROOT_PATHS,
  ROUTES,
  SCOPED_PATHS,
  SIGN_IN_PATH,
  SUSPENDED_PATH,
  WELCOME_PATH,
} from './routes';
import { SessionProvider, useSession } from './session';
import {
  resolveWorkspaceAddress,
  useRenamedSlugs,
  withFirstSegment,
  scoped,
  useWorkspacePath,
  withoutWorkspacePrefix,
  workspacePath,
} from './workspace-path';
import { useAvatar, useAvatars } from './screens/use-avatar';
import { useCommandPalette } from './screens/use-command-palette';
import { WorkspaceDialog } from './screens/workspaces/workspace-dialog';

/** C42 · one place that knows how a workspace's picture is addressed. */
const iconPathOf = (workspaceId: string): string => `/files/workspace-icon/${workspaceId}`;
import { PreferencesBoot } from './preferences-boot';
import { DurationProvider, useDuration } from './duration';
import { useSaveDurationUnit } from './duration-toggle';
import { LiveProvider } from './live/live-provider';
import { useNotifications } from './screens/notifications/use-notifications';
import { SidebarProvider, useSidebarContext } from './screens/sidebar/use-sidebar';
import { DemoBanner } from './screens/demo/demo-banner';
import { WelcomeCards } from './screens/onboarding/welcome-cards';
import { useOnboarding } from './onboarding/use-onboarding';
import { Tour } from './onboarding/tour';
import { sidebarRoutes, sidebarRows } from './screens/sidebar/sidebar-rows';
import { PageHeaderProvider, PlainTitle, usePageHeaderNode } from './screens/page-header';
import { ListLayoutsProvider } from './list-layouts';
import { TrackerConnectionProvider, useTrackerConnection } from './tracker-connection';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The router, and the entrance every screen has to have.                    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **BOTH HALVES ARE DRIVEN BY `routes.tsx`, and that is the whole design.** CLAUDE.md §4: *"a
 * route that renders is not a route anybody can reach"* — seven screens once shipped reachable only
 * by typing a URL. A navigation written by hand beside a route table written by hand drifts on the
 * first screen somebody adds in a hurry, and the drift is invisible: both halves are individually
 * correct.
 *
 * So the sidebar below is `ROUTES.filter(entrance === 'sidebar')`. Adding a screen with a sidebar
 * entrance gives it a link with no further work, and adding one *without* an entrance requires
 * writing down why (`unreachableBecause`). The reachability test reads the same table.
 *
 * ⚠️ **`react-router` is an addition** — nothing in the mockup or `decisions.md` chose a router,
 * because the mockup is one file with a `view` variable. Recorded in `decisions.md` rather than
 * assumed here.
 */

export function App({ locale }: { readonly locale?: Locale } = {}): ReactNode {
  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ STATE, BECAUSE THE LANGUAGE IS A STORED PREFERENCE AND THE FIRST     │
   * │ FRAME HAS TO BE DRAWN BEFORE THE SERVER CAN BE ASKED WHAT IT IS.          │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * `preferredLocale()` is the guess — the hint this device last saw, then `navigator.languages`,
   * then Hungarian (R-6). `PreferencesBoot` corrects it from `user_preferences.locale` once the
   * session and the workspace exist, and the account screen moves it when somebody picks a
   * language. All three go through this one setter, so there is exactly one answer to *"what
   * language is this app in"* at any moment.
   *
   * ⚠️ **This used to be `const resolved = locale ?? preferredLocale()`**, and the comment beside it
   * said a locale change would be a remount — which was true because nothing could change it. The
   * consequence was the defect this replaces: the interface followed the browser, the API followed
   * the stored setting, and the Agent answering in Hungarian to an English question was the
   * visible half.
   *
   * ⚠️ **An explicit `locale` prop pins it and suppresses the correction**, which is what the
   * suites want: a test that renders at `en` must not have a stubbed `/account` move it to `hu`
   * halfway through an assertion.
   */
  const pinned = locale !== undefined;
  const [resolved, setResolved] = useState<Locale>(locale ?? preferredLocale());

  return (
    /* ⚠️ 3 Nov 2026 · outside the i18n provider, which reads the unit to pick a `.days` label
       (`duration.tsx`, `decisions.md` DU-1). */
    <DurationProvider>
      <I18nProvider locale={resolved} onLocaleChange={pinned ? undefined : setResolved}>
        {/* ⚠️ The session sits INSIDE the i18n provider, because a sign-in failure has to be
          rendered in words — and outside the router, because restoring a session is not a route. */}
        <SessionProvider>
          {/* ⚠️ Above the router and inside the session: the stored theme is applied once per
            sign-in, so switching it never remounts a screen or loses view state. */}
          <PreferencesBoot />
          {/* ⚠⚠️ C21 · ONE stream for the whole tab, and it has to be here. Inside the router it
            would be re-opened by every navigation; outside the session it would have no token to
            open with. `decisions.md` R-19. */}
          <LiveProvider>
            {/* Above `Gate`, because the welcome screen that starts it is unmounted by its own
              success — `welcome-transition.tsx` says why. */}
            <WelcomeTransitionProvider>
              <Gate />
            </WelcomeTransitionProvider>
          </LiveProvider>
        </SessionProvider>
      </I18nProvider>
    </DurationProvider>
  );
}

/**
 * ⚠️ **Three states, and conflating any two of them is a bug somebody reports as "it hangs".**
 *
 *  * `restoring` — we are asking the API whether the refresh cookie still works. Rendering the
 *    sign-in screen here would flash a login form at somebody who is signed in, on every reload;
 *  * `signed-out` — the sign-in screen, whatever the address was;
 *  * `signed-in` — the application.
 *
 * The first is the one that gets skipped, and skipping it is why "log in again on every refresh" is
 * such a common complaint about apps that keep their token in memory.
 */
function Gate(): ReactNode {
  const t = useT();
  const session = useSession();
  const location = useLocation();
  const navigate = useNavigate();

  /**
   * ⚠️⚠️ **BEFORE THE THREE STATES, BECAUSE THIS ROUTE BELONGS TO NONE OF THEM.** An invitation link
   * is opened by somebody who may have no account, one account, or an account in a different
   * workspace — and the page has to render for all three, because what it says (*"X invited you to
   * Y, sign in with this address"*) is what makes the next step possible. Below this point the
   * signed-out branch renders the sign-in screen for every address, which would swallow it.
   *
   * ⚠️ **`matchPath`, not an equality test**, because the path carries the token. The `find` in
   * `Chrome` compares `route.path` to the pathname exactly — fine for every screen with a static
   * address, and silently false for this one.
   */
  /**
   * ⚠️⚠️ **SIGNING IN LANDS ON THE HOME SCREEN, AND THIS IS AN EFFECT BECAUSE A REF MUTATED DURING
   * RENDER DOES NOT SURVIVE `StrictMode`.** The first version read and wrote the flag in the render
   * body. React invokes a component's render **twice** in development and keeps the SECOND result —
   * so pass one saw "was signed out" and returned a redirect, pass two saw the flag already cleared
   * and returned the screen, and the redirect was silently discarded. It worked from `/sign-in` (the
   * chrome bounces that address anyway) and did nothing from `/tasks`, which is exactly how it was
   * reported.
   *
   * An effect runs after the commit, once per real state change, and is the sanctioned place for
   * this kind of bookkeeping.
   *
   * ⚠️⚠️ **ONLY FROM `signed-out`, NEVER FROM `restoring`.** Every page load starts in `restoring`
   * while the refresh cookie is checked; treating that as a fresh sign-in would bounce somebody home
   * **on every refresh**, losing the page they were reading. That is a worse bug than the one this
   * fixes and an easy one to write by accident, so the flag is set only by a real signed-out render.
   *
   * ⚠️ The cost, stated: a deep link followed while signed out loses its destination. Restoring the
   * intended address is a genuine refinement and is not this.
   */
  const wasSignedOut = useRef(false);
  useEffect(() => {
    if (session.status === 'signed-out') {
      wasSignedOut.current = true;
      return;
    }
    if (session.status === 'signed-in' && wasSignedOut.current) {
      wasSignedOut.current = false;
      /**
       * ⚠️⚠️ **R-38 · `'/'`, NOT `HOME_PATH` — BECAUSE AT THIS INSTANT WE DO NOT KNOW THE
       * WORKSPACE.** `/auth/me` has answered, but which workspace to enter is
       * `resolveWorkspaceAddress`'s decision and it needs the remembered id, the membership list
       * and the address. Navigating to `/home` here would produce a bare, unscoped address that
       * then has to be rescued; navigating to `/` asks the one function that knows, and it
       * redirects to `/{workspace}/home`. One place decides where a session lands.
       */
      /**
       * ⚠️⚠️ 21 Oct 2026 · **NOT WHEN WE ARE ALREADY THERE — THE BLANK PAGE AFTER SIGN-IN.** Signed
       * in at the bare `/` (a first login almost always is), `WorkspaceScope` had already rendered
       * its `<Navigate to="/{workspace}/home">`, and this replace back to `/` landed in the same
       * transition — the router settled on `/`, the `<Navigate>` stayed mounted with unchanged
       * inputs and never fired again, and the page was empty until a refresh.
       */
      if (location.pathname !== '/') void navigate('/', { replace: true });
    }
  }, [session.status, navigate, location.pathname]);

  const openRoute = ROUTES.find(
    (route) => route.signedOutOk === true && matchPath(route.path, location.pathname) !== null,
  );
  if (openRoute !== undefined) {
    /**
     * ⚠️⚠️ **THROUGH A `<Route>`, NOT AS A BARE ELEMENT — AND THIS WAS A REAL BUG, NOT A PRECAUTION.**
     * `useParams()` reads the router's MATCH, so an element rendered outside any `<Route>` gets an
     * empty params object. The first version was `return <>{openRoute.element}</>`, and every
     * invitation link opened onto *"that link is not valid"*: the screen read `token` as `undefined`,
     * asked the API for `/invitations/` and got a 404.
     *
     * ⚠️ **And the route test could not see it.** It asserts each address renders its own
     * `data-screen` marker — which the invalid-link state also carries, because it is the same page.
     * The case that catches it has to assert the CONTENT the token produces.
     */
    return (
      <Routes>
        <Route path={openRoute.path} element={openRoute.element} />
      </Routes>
    );
  }

  if (session.status === 'restoring') {
    return (
      <div className="grid min-h-screen place-items-center">
        <p role="status" className="text-muted-foreground text-[13px]">
          {t('session.restoring')}
        </p>
      </div>
    );
  }

  if (session.status === 'signed-out') {
    /**
     * ⚠️ **Rendered rather than redirected**, so the address bar is not rewritten while somebody is
     * still typing their password. Where they go once they succeed is decided by the effect above,
     * which sends a fresh sign-in to `HOME_PATH`.
     *
     * ⚠️⚠️ **BUT THE SIGNED-OUT SCREEN AT *THIS* ADDRESS WINS, AND WITHOUT THAT `/sign-up` COULD NOT
     * EXIST.** This branch used to render the sign-in screen for every address unconditionally — so
     * navigating to `/sign-up` while signed out rendered the login form, and the new screen would
     * have been reachable by nothing at all. `signedOutOnly` is the route's own declaration that it
     * belongs here; sign-in stays the fallback for every other address.
     */
    const here = ROUTES.find(
      (route) => route.signedOutOnly === true && route.path === location.pathname,
    );
    const signIn = ROUTES.find((route) => route.path === SIGN_IN_PATH);
    return <>{(here ?? signIn)?.element}</>;
  }

  /**
   * ⚠️⚠️ **SIGNED IN AND IN NO WORKSPACE IS ITS OWN STATE, AND IT USED TO BE A DEAD END.** Before
   * this branch, such an account fell through to `Chrome`: the shell rendered with
   * `nav.noWorkspace` in the workspace slot, every data screen answered `state.noWorkspace`, and the
   * only way forward was for somebody already inside a workspace to send an invitation — which is
   * not available to the first person through the door. `decisions.md` S-2 makes sign-up self-serve,
   * so there has to be a screen here.
   *
   * ⚠️ **Rendered IN PLACE, not redirected, exactly as the signed-out branch is.** A redirect would
   * rewrite the address they came for, and they should land back on it once the workspace exists —
   * which happens by itself, because `reload()` fills the workspace in and this branch stops
   * matching.
   *
   * ⚠️ **Through a `<Route>`, for the reason the `signedOutOk` branch above records at length**:
   * an element rendered outside any `<Route>` gets an empty `useParams()`. This screen reads no
   * params today, and the invitation page shipped that bug once already — so it goes through a
   * route rather than relying on a fact about the screen that a later edit could change.
   */
  /**
   * ⚠️ `0202` · **AND WITH A WORKSPACE WHOSE SETUP IS UNFINISHED** — the admin who created the
   * workspace in step 2 and has not reached step 4. Same branch, same element, same position, so
   * the session reload between steps 2 and 3 re-renders the setup screen rather than remounting it.
   */
  if (session.workspaceId === null || !session.setup.finished) {
    const welcome = ROUTES.find((route) => route.path === WELCOME_PATH);
    return (
      <Routes>
        <Route path="*" element={welcome?.element} />
      </Routes>
    );
  }

  /**
   * ⚠️⚠️ **R-38 · WHICH WORKSPACE THIS ADDRESS IS ABOUT IS DECIDED IN ITS OWN COMPONENT, AND THE
   * SPLIT IS FORCED BY THE RULES OF HOOKS.** The resolution needs an effect — adopting the
   * workspace an address names is a state change, and doing it in a render body does not survive
   * `StrictMode`, which this file already records at length for `wasSignedOut`. Every branch above
   * is an early return, so a hook added here would be conditional. A child component is where the
   * hooks can live unconditionally.
   */
  return <WorkspaceScope />;
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ R-38 · the address says which workspace, and this is where it is read.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE URL IS THE SOURCE OF TRUTH FROM HERE DOWN.** `session.workspaceId` still holds the
 * answer — it is what `x-workspace-id` is taken from — but the address is what SETS it, and
 * `localStorage` has been demoted to answering *"where do I go when the address does not say"*.
 * `workspace-path.ts` carries the argument for the inversion.
 */
function WorkspaceScope(): ReactNode {
  const t = useT();
  const session = useSession();
  const location = useLocation();
  const { selectWorkspace } = session;

  const held = session.workspaces.find((entry) => entry.id === session.workspaceId) ?? null;

  /**
   * 26 Oct 2026 · an address naming a slug this workspace had a moment ago — the general settings
   * just saved a new one. It is read as the new address, so the chrome and the screen that saved
   * stay mounted, and the effect below puts the new address in the bar (`useRenamedSlugs`).
   */
  const navigate = useNavigate();
  const renamed = useRenamedSlugs(session.workspaces);
  const firstSegment = location.pathname.split('/').find((segment) => segment !== '') ?? '';
  const renamedTo = renamed.get(firstSegment) ?? null;
  const pathname =
    renamedTo === null ? location.pathname : withFirstSegment(location.pathname, renamedTo);
  useEffect(() => {
    if (renamedTo === null) return;
    void navigate(`${pathname}${location.search}${location.hash}`, { replace: true });
  }, [renamedTo, pathname, location.search, location.hash, navigate]);

  const address = resolveWorkspaceAddress({
    pathname,
    slugs: session.workspaces.map((entry) => entry.slug),
    /* ⚠️ The workspace the session is already holding — which `load()` seeded from the remembered
       id, or from the first membership. This is the ONLY thing that remains of the old behaviour,
       and it now answers one narrow question rather than deciding. */
    fallback: held?.slug ?? null,
    home: HOME_PATH,
    rootPaths: ROOT_PATHS,
    scopedPaths: SCOPED_PATHS,
  });

  /**
   * ⚠️⚠️ **THE SESSION IS MOVED TO THE WORKSPACE THE ADDRESS NAMES, AND THE RENDER IS HELD UNTIL IT
   * HAS.** Not holding it is a real bug rather than a tidiness point: every screen fires its read
   * on mount, `request()` stamps `x-workspace-id` from `session.workspaceId`, and rendering the
   * shell one tick early means those reads go out naming the *previous* workspace. The person then
   * sees another workspace's projects under this one's name — which is precisely the symptom C42
   * fixed for the switcher, arriving again through the address bar.
   */
  const wanted =
    address.kind === 'enter'
      ? (session.workspaces.find((entry) => entry.slug === address.slug) ?? null)
      : null;
  const wantedId = wanted?.id ?? null;
  const moving = wantedId !== null && wantedId !== session.workspaceId;

  useEffect(() => {
    /* ⚠️ `moving` already implies a non-null id — it is defined as `wantedId !== null && …` — so a
       second null check here is the kind of redundant guard the linter is right to refuse. */
    if (moving) selectWorkspace(wantedId);
  }, [moving, wantedId, selectWorkspace]);

  /* ⚠️ `replace`, so a redirect does not put an address nobody typed into the back button. */
  /* ⚠️ Keyed on the navigation, so arriving back at an address that redirects always redirects —
     a `<Navigate>` that stays mounted with the same inputs never fires twice (21 Oct 2026). */
  /* ⚠️ 3 Nov 2026, e2e audit R34 · **the query and the fragment come along.** The OAuth callback
     cannot know the workspace when it refuses a state, so it sends `/account?connected=state` and
     relies on this rescue — which kept the path and dropped the outcome, leaving a profile page
     with nothing to say. A bookmarked filter is the same loss, quieter. */
  if (address.kind === 'redirect') {
    return (
      <Navigate key={location.key} to={`${address.to}${location.search}${location.hash}`} replace />
    );
  }

  /**
   * ⚠️⚠️ **ONE SCREEN FOR "NO SUCH WORKSPACE" AND "NOT YOURS", AND THAT IS RULE 5 APPLIED TO A
   * PATH.** A record in another workspace is `not_found` and never `permission_denied`, because
   * existence is itself information. The client is in the same position by construction — it knows
   * only its own memberships — so there is nothing here that *could* distinguish the two, and the
   * copy must not imply it can.
   */
  if (address.kind === 'unknown') return <UnknownWorkspace />;

  if (moving) {
    return (
      <div className="grid min-h-screen place-items-center">
        <p role="status" className="text-muted-foreground text-[13px]">
          {t('session.restoring')}
        </p>
      </div>
    );
  }

  /**
   * ⚠️⚠️ **SIGNED IN, IN A WORKSPACE, AND THE TENANT IS SUSPENDED** (`decisions.md` S-9). The only
   * branch here a *fully valid* session reaches: the token is live, the session is registered, the
   * membership is active — the tenant is not.
   *
   * ⚠️ **Rendering the shell instead would be a page of controls that all 403.** Every
   * workspace-scoped request is refused by the guard, so the sidebar's counters, the notification
   * bell and each screen's own read would all fail at once. That is the state somebody reports as
   * *"the app is broken"*, and it is why the API answers a distinguishable `tenant_suspended`
   * instead of a 404: this branch is only possible because the client can tell.
   *
   * ⚠️ **Rendered in place, not redirected**, for the reason the two branches above are: the address
   * they came for is still where they belong once the suspension is lifted, and it comes back by
   * itself — `/auth/me` stops saying `suspended` and this branch stops matching.
   */
  const suspended = session.workspaces.find((entry) => entry.id === session.workspaceId);
  if (suspended?.tenantStatus === 'suspended') {
    const screen = ROUTES.find((route) => route.path === SUSPENDED_PATH);
    return (
      <Routes>
        <Route path="*" element={screen?.element} />
      </Routes>
    );
  }

  /**
   * ⚠️ **The provider wraps `Chrome`, not `App`.** Everything inside it needs a session and a
   * workspace — `/workspace/sidebar` is workspace-scoped — and the signed-out screens above this
   * point have neither. `useSidebarContext()` returns null there, which is what a star on a page
   * with no sidebar should be: absent.
   */
  return (
    <SidebarProvider>
      {/* ⚠️ Inside the sidebar provider so a screen can use both; outside `Chrome` so the header
          slot survives a route change rather than being re-created with the bar that reads it. */}
      <PageHeaderProvider>
        {/* 20 Oct 2026 · LV-1 · every list's table-or-cards and card layout, read once. */}
        <ListLayoutsProvider>
          {/* 21 Oct 2026 · a missing Jira connection, raised on every hour logged until it exists. */}
          <TrackerConnectionProvider>
            <Chrome />
          </TrackerConnectionProvider>
        </ListLayoutsProvider>
      </PageHeaderProvider>
    </SidebarProvider>
  );
}

/**
 * R-38 · the address named a workspace this person is not in.
 *
 * ⚠️⚠️ **IT SAYS ONE THING FOR TWO CAUSES, AND THAT IS DELIBERATE.** The workspace may not exist,
 * or it may exist and not be theirs. CLAUDE.md rule 5 — a record in another workspace is
 * `not_found`, never `permission_denied`, because existence is itself information — and the client
 * could not tell them apart anyway: it knows its own memberships and nothing else. Copy that said
 * *"you do not have access to this workspace"* would confirm the workspace exists.
 *
 * ⚠️ **A way out, not just a refusal.** The switcher is in the shell and the shell is not rendered
 * here, so without this link somebody following a colleague's URL is looking at a dead end.
 */
function UnknownWorkspace(): ReactNode {
  const t = useT();
  const session = useSession();
  const held = session.workspaces.find((entry) => entry.id === session.workspaceId) ?? null;

  return (
    <div data-screen="unknown-workspace" className="grid min-h-screen place-items-center p-8">
      <div className="max-w-[420px] text-center">
        <p className="text-[15px] font-medium">{t('workspace.unknown.title')}</p>
        <p className="text-muted-foreground mt-2 text-[13px]">{t('workspace.unknown.body')}</p>
        {held !== null && (
          <Link
            to={workspacePath(held.slug, HOME_PATH)}
            className="text-primary mt-4 inline-block text-[13px] underline"
          >
            {t('workspace.unknown.back', { workspace: held.name })}
          </Link>
        )}
      </div>
    </div>
  );
}

/**
 * Two initials for the sidebar chip.
 *
 * ⚠️ **From the NAME when there is one — first and last word, the rule every other avatar in
 * this app uses.** Cutting them from the email gives *"ga"* for `gabor.takacs@…`, which is not
 * what anybody's colleagues call them, and does not match the circle beside their name in a list.
 */
function shellInitials(name: string | null, email: string | null): string {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  const first = words[0]?.[0] ?? '';
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : '';
  const cut = `${first}${last}`;
  return cut === '' ? (email ?? '?').slice(0, 2).toUpperCase() : cut.toUpperCase();
}

const SIDEBAR_KEY = 'app.sidebar.collapsed';
/** The last address outside settings — where the settings panel's back arrow returns to. */
const SETTINGS_ORIGIN_KEY = 'app.settings.origin';

/** One row of the settings panel, before it becomes a link. */
interface SettingsEntry {
  readonly key: string;
  readonly label: string;
  readonly icon: NonNullable<NavItem['icon']>;
  readonly path: string;
  readonly active: boolean;
  readonly marker?: string;
  readonly warning?: string;
}

function Chrome(): ReactNode {
  const t = useT();
  const location = useLocation();
  const navigate = useNavigate();
  const session = useSession();
  /**
   * ⚠️ R-38 · every address the shell renders is built through this. `route.path` is a pattern
   * (`/:workspace/tasks`); this turns a workspace-relative path into the concrete address for the
   * workspace currently in the URL, which is the only one the shell is ever rendered for.
   */
  const here = useWorkspacePath();
  const avatar = useAvatar(session.avatarUrl);
  /* 3 Nov 2026 · the man-day switch in the user menu (`decisions.md` DU-1). */
  const { unit: durationUnit, fmt: fmtDuration } = useDuration();
  const saveDurationUnit = useSaveDurationUnit();
  /**
   * C42 · ⚠️ **The switcher's own state lives in the chrome, because the chrome is where it is
   * used.** A route would give it an address nobody would ever paste, and a context would be one
   * provider for one boolean.
   */
  const [creatingWorkspace, setCreatingWorkspace] = useState(false);
  /* ⚠️ Every workspace's icon, resolved in one hook — `useAvatars` takes the list rather than being
     called per row, because a hook cannot be called in a loop. */
  const workspaceIcons = useAvatars(
    session.workspaces.map((entry) => (entry.hasIcon ? iconPathOf(entry.id) : null)),
  );

  /**
   * ⚠️ **The document title, because rule 7 names page titles explicitly.** A tab that says
   * "Forecastify" on every screen is a tab nobody can use to find the window they left open — and
   * it is user-facing text, so it is a catalogue key like any other.
   */
  /**
   * ⚠️⚠️ **`matchPath`, NOT `route.path === location.pathname`.** The equality version shipped and
   * silently gave every PARAMETERISED route the bare app name: `/tasks/HSO-1`, `/projects/<uuid>`,
   * `/contracts/HSO-C01` and every other `:param` address matched no row, so the effect returned
   * early and the tab kept whatever title the previous screen left there. Compiled, rendered, and
   * wrong on roughly a third of the app — found in C32 by reading the tab on an extension's page.
   */
  const current = ROUTES.find((route) => matchPath(route.path, location.pathname) !== null);
  useEffect(() => {
    if (current === undefined) return;
    document.title = t(current.title);
  }, [current, t]);

  /**
   * ⚠️ **A `'bare'` route renders with NO chrome around it**, because the screen brings its own —
   * see the note in `routes.tsx`. Two nested shells means two `<nav>` landmarks on one page, which
   * is an accessibility defect and made the reachability test ambiguous about which sidebar it was
   * asking about.
   */
  /**
   * The bell. C17.
   *
   * ⚠️⚠️ **CALLED BEFORE THE `'bare'` RETURN BELOW, BECAUSE HOOKS DO NOT SURVIVE AN EARLY EXIT.**
   * Moving it down to where the shell is built would make the hook order depend on the route, which
   * React reports as a different error on every navigation.
   *
   * ⚠️ The navigation is by task KEY, because that is what a person recognises and what
   * `/tasks/:key` takes — the notification carries both the id and the key for this reason.
   */
  /**
   * The chrome's own read: five counters and the two pinned lists. C18.
   *
   * ⚠⚠️ **CALLED HERE, BEFORE THE `'bare'` RETURN BELOW, FOR THE SAME REASON THE BELL IS.**
   * Hooks do not survive an early exit — moving either below the branch would make the hook order
   * depend on the route, which React reports as a different error on every navigation.
   */
  const pageHeader = usePageHeaderNode();
  const sidebarHandle = useSidebarContext();
  /* F-1 · read here, beside the sidebar, for the same before-the-early-return reason. */
  const onboarding = useOnboarding();
  /**
   * F-2 · the tour that is running, if any — started from the welcome cards' last card or from the
   * user menu. ⚠️ Its conditions read the effective permissions (`/permissions/me`), never a role's
   * name; they wait for the read rather than guessing.
   */
  const [touring, setTouring] = useState(false);
  const tourPermissions = usePermissionSet();
  /* ⚠️ `?? EMPTY`-shaped fallback rather than a throw: `Chrome` is always inside the provider, but a
   * null-safe read keeps this component renderable in isolation by a test. */
  const sidebarView =
    sidebarHandle?.view ??
    ({
      counts: {
        projects: null,
        clients: null,
        people: null,
        tasks: null,
        contracts: null,
        invoices: null,
        reports: null,
        worklogs: null,
        myOpenTasks: null,
        pendingApprovals: null,
        requests: null,
        deals: null,
      },
      projects: [],
      reports: [],
      deals: [],
      /* ⚠️ Empty, so a chrome rendered in isolation draws no flagged row — the same fail-closed
       * reading `FeatureService` applies to a key it cannot resolve. Absence is off. */
      features: {},
      /* ⚠️ *Not answered* rather than *no*, like `features` above and like `EMPTY` — a chrome with
         no provider has asked nobody, and a nav that draws and then rearranges is the failure both
         defaults exist to avoid. */
      entitlements: {
        logsTime: true,
        leaveRequest: true,
        leaveDecide: true,
        leaveViewAll: true,
        menuProjects: true,
        menuTasks: true,
        menuContracts: true,
        menuInvoices: true,
        menuWorklogs: true,
        menuPeople: true,
        menuClients: true,
        reports: true,
        dashboardResource: true,
        dashboardLogging: true,
        dashboardUtilization: true,
        dashboardLeave: true,
      },
      demoData: false,
    } satisfies SidebarView);

  const bell = useNotifications(
    (key) => {
      /* ⚠️ R-38 · `here(...)`, because the notification carries a task KEY and not an address.
         The bell is in the shell, so the workspace it should open in is the one in the URL. */
      void navigate(here(`/tasks/${key}`));
    },
    (id) => {
      /* `0183` · a leave request or an overtime claim — the one detail screen opens both. */
      void navigate(here(`/requests/${id}`));
    },
    (path) => {
      /* `0186` · a project or a contract, by the address the row names. */
      void navigate(here(path));
    },
  );

  /**
   * ⚠️⚠️ **THE SIDEBAR ROWS AND THE PALETTE LIVE ABOVE THE EARLY RETURNS, AND THE LINTER IS
   * RIGHT TO INSIST.** Four `if (…) return <Navigate …>` guards follow this line, so a hook
   * declared after them runs on some renders and not others — `rules-of-hooks` catches it, and
   * what it prevents is `useState` returning another hook's state the render after a redirect.
   *
   * ⚠️ `sidebar` is a plain filter over a module constant and could sit anywhere; it is here
   * because the palette takes it, and a reader should not have to look in two places to see what
   * ⌘K contains.
   */
  /* ⚠️ The same list the rows are drawn from — `sidebar-rows.ts` — so ⌘K cannot contain a screen
     the sidebar hides. */
  const sidebar = sidebarRoutes(sidebarView);

  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ ⌘K, WIRED AT LAST - item 71.
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * The note that stood on `onOpenSearch` said it all: *"`CommandDialog` exists (C2) but has no
   * content to search - the palette's own contents are the mockup's navigation plus actions, and
   * half of those actions are unbuilt chunks. So the control jumps to the task list, which is the
   * nearest true thing, and the search palette arrives with the chunk that owns it."* This is that
   * chunk. The button opens the palette; nothing navigates to `/tasks` any more.
   *
   * ⚠️⚠️ **THE NAVIGATION ROWS ARE THE SIDEBAR'S OWN, PASSED IN RATHER THAN REBUILT.** They come
   * from `ROUTES` twelve lines above; a second builder inside the hook is how two lists come to
   * disagree about what the app contains, and the palette is the one place where a stale entry
   * looks exactly like a working one until it is pressed.
   */
  const [paletteOpen, setPaletteOpen] = useState(false);

  /* ⚠️ The sidebar's width, remembered per browser — the Control Panel's own arrangement
     (`cp.sidebar.collapsed`). Wrapped, because storage throws in a locked-down or private window
     and a sidebar that cannot remember is still a sidebar. */
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_KEY) === '1';
    } catch {
      return false;
    }
  });
  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ SETTINGS MODE — 13 Oct 2026, the product owner.                      │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * *"Entering the settings menu overrides the sidebar menu with the settings menu's content, search
   * is limited to the settings' search, workspace changer is replaced by 'Back to the workspace', and
   * the sidebar isn't collapsible."* Both kinds of settings — the workspace's (`/settings…`) and the
   * person's own (`/account`) — and each screen dropped the rail it used to draw beside ours.
   *
   * ⚠️⚠️ **RESHAPED 14 Oct 2026, AFTER SHOPIFY'S ADMIN.** The sidebar no longer swaps its content:
   * it narrows to its icon rail — every screen still one hover away — and the settings menu opens
   * as a panel BESIDE it, with a back arrow to wherever the person was. `AppShell`'s `settings`.
   *
   * ⚠️ **The rows are the sections' own declarations** (`SETTINGS_SECTIONS`, `ACCOUNT_NAV`), so the
   * sidebar and the narrow-screen pickers cannot disagree about what a settings area contains.
   * Workspace sections are filtered by the reader's permissions, as the rail filtered them.
   */
  /* ⚠️ From the MATCHED route's pattern — `withoutWorkspacePrefix` strips `/:workspace`, not a slug. */
  const relativePath = current === undefined ? '' : withoutWorkspacePrefix(current.path);
  const settingsMode: 'settings' | 'account' | null =
    relativePath === '/settings' || relativePath.startsWith('/settings/')
      ? 'settings'
      : relativePath === '/account'
        ? 'account'
        : null;
  const permissionSet = usePermissionSet();
  const accountDirty = useAccountDirty();
  /* 21 Oct 2026 · a Jira instance this person has not connected — marked on the chip and the row. */
  const trackerMissing = useTrackerConnection().missing.length > 0;
  const accountSection = new URLSearchParams(location.search).get('section') ?? 'profile';
  /**
   * ⚠️⚠️ **BOTH LISTS, WHICHEVER KIND OF SETTINGS IS OPEN — 14 Oct 2026.** The panel beside the rail
   * holds the workspace's sections and the person's own in two fixed groups (Shopify's organisation
   * and store), so moving from *Általános* to *Biztonság* is one click in one menu. Only the open
   * kind marks a row active.
   */
  const workspaceSettings: readonly SettingsEntry[] = SETTINGS_SECTIONS.filter((entry) =>
    permissionSet.has(entry.requires),
  ).map((entry) => ({
    key: entry.key,
    label: t(entry.label),
    icon: entry.icon,
    path: sectionPath(entry.key),
    /* `/settings` is Általános; `/settings/permissions/:roleId` is still Jogosultságok. */
    active: settingsMode === 'settings' && (relativePath.split('/')[2] ?? 'general') === entry.key,
  }));
  const accountSettings: readonly SettingsEntry[] = ACCOUNT_NAV.map((entry) => ({
    key: entry.id,
    label: t(entry.label),
    icon: entry.icon,
    path: `/account?section=${entry.id}`,
    active: settingsMode === 'account' && accountSection === entry.id,
    ...(accountDirty[entry.id] === true ? { marker: t('account.section.unsaved') } : {}),
    ...(entry.id === 'integrations' && trackerMissing
      ? { warning: t('trackerConnection.missing') }
      : {}),
  }));
  /* ⚠️ The two kinds of settings stay SEPARATE (14 Oct 2026): the workspace menu opens the
     workspace's, the person's menu opens their own, and each panel and search lists only its own. */
  const settingsEntries = settingsMode === 'account' ? accountSettings : workspaceSettings;

  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ WHERE THE PANEL'S BACK ARROW GOES — where the person was, not Home.   │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * The product owner: *"the top back arrow takes you back to where you were"*. Every address the
   * person visits OUTSIDE settings is remembered; moving between settings sections is not, so three
   * sections later the arrow still leads to the task list they left. History would not do: it would
   * step back through the sections.
   *
   * ⚠️ **`sessionStorage`, so a reload inside settings still knows** — and per tab, because two tabs
   * came from two places. Wrapped, for the private-window reason the sidebar's own key is.
   * ⚠️ Only an address in THIS workspace is used; anything else falls back to Home, so a stale
   * entry can never send the arrow into the workspace somebody just switched away from.
   */
  const settingsOrigin = useRef<string | null>(null);
  if (settingsOrigin.current === null) {
    try {
      settingsOrigin.current = sessionStorage.getItem(SETTINGS_ORIGIN_KEY) ?? '';
    } catch {
      settingsOrigin.current = '';
    }
  }
  useEffect(() => {
    if (settingsMode !== null || current === undefined) return;
    const at = `${location.pathname}${location.search}`;
    settingsOrigin.current = at;
    try {
      sessionStorage.setItem(SETTINGS_ORIGIN_KEY, at);
    } catch {
      /* Not remembered past a reload; the arrow falls back to Home. */
    }
  }, [settingsMode, current, location.pathname, location.search]);

  /**
   * ⚠️ **⌘K / Ctrl+K opens the palette** — the hint the search button has always shown, and in
   * settings the palette is the settings' own (`scope` below).
   */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key.toLowerCase() !== 'k' || !(event.metaKey || event.ctrlKey)) return;
      event.preventDefault();
      setPaletteOpen(true);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const palette = useCommandPalette({
    open: paletteOpen,
    /* ⚠️ In settings mode the palette searches the settings and nothing else. */
    scope: settingsMode === null ? 'app' : 'settings',
    /**
     * 17 Oct 2026 · in settings, the headings inside the sections this reader can open, and the
     * area whose entities the server searches (`settings-search-index.ts`, `GET /search/settings`).
     */
    ...(settingsMode === null
      ? {}
      : {
          settings: {
            area: settingsMode === 'account' ? ('account' as const) : ('workspace' as const),
            headings: settingsEntries.flatMap((entry) =>
              (settingsMode === 'account'
                ? ACCOUNT_HEADINGS[entry.key as AccountSectionId]
                : (WORKSPACE_HEADINGS[entry.key] ?? [])
              ).map((key) => ({
                label: t(key),
                section: entry.label,
                icon: entry.icon,
                path: entry.path,
              })),
            ),
          },
        }),
    navigation:
      settingsMode === null
        ? sidebar.map((route) => ({
            label: route.navLabel === undefined ? t(route.title) : t(route.navLabel),
            icon: route.icon ?? ('folder' as const),
            /* ⚠️ The WORKSPACE-RELATIVE path - `go` applies the prefix, as everywhere in this file. */
            path: withoutWorkspacePrefix(route.path),
          }))
        : settingsEntries.map((entry) => ({
            label: entry.label,
            icon: entry.icon,
            path: entry.path,
          })),
    go: (path) => {
      setPaletteOpen(false);
      void navigate(here(path));
    },
    /* 3 Nov 2026 · a create action only where its list opens and the create is held. */
    actions: {
      newTask: sidebarView.entitlements.menuTasks && permissionSet.has('task.create'),
      newProject: sidebarView.entitlements.menuProjects && permissionSet.has('project.create'),
    },
  });

  /**
   * ⚠️⚠️ **A SIGNED-IN PERSON IS NOT ALLOWED TO SIT ON A SIGNED-OUT SCREEN, AND THIS WAS A REPORTED
   * BUG.** Signing in while the address IS `/sign-in` left the form on screen — status `signed-in`,
   * session established, looking at a login page. It reads exactly like the sign-in having failed,
   * and it is what the product owner hit: *"I tried logging in but it just put me back onto the sign
   * in screen?"*
   *
   * ⚠️ **It never happened before C17 because nothing ever NAVIGATED to `/sign-in`.** `Gate`'s
   * signed-out branch renders the sign-in screen *in place*, at whatever address somebody came for —
   * so after signing in they land on that address and the form disappears with the state that
   * produced it. The invitation page added the first two controls in the app that go to `/sign-in` as
   * an address, and the hole opened under them.
   *
   * ⚠️ **Now driven by `signedOutOnly` rather than by a comparison per path.** It covers `/sign-in`
   * and S2's `/sign-up`, and the next one costs a field rather than an edit here, in
   * `routes.test.tsx` and in `Gate`.
   */
  /* ⚠️ R-38 · `here(...)`, not the bare constant: a bounce to `/home` resolves to a workspace
     called `home` and lands on "that address does not lead anywhere". These three are the only
     `Navigate`s in the shell, and all three were bare. */
  if (current?.signedOutOnly === true) return <Navigate to={here(HOME_PATH)} replace />;

  /**
   * ⚠️ **`/welcome` is a SEPARATE condition, not a third `signedOutOnly` route.** It is for somebody
   * who *is* signed in and belongs to no workspace — reaching `Chrome` at all means the session has
   * a workspace, so this address is spent: the screen would offer to create one to somebody already
   * in one, and `POST /tenants` would refuse them `already_member`. Folding it into the flag would
   * make the flag mean two different things.
   */
  if (current?.path === WELCOME_PATH) return <Navigate to={here(HOME_PATH)} replace />;

  /**
   * ⚠️ **And `/suspended` for the same reason, from the other direction.** Reaching `Chrome` means
   * the guard is letting requests through, so the tenant is not suspended and this address is spent:
   * it would tell somebody their healthy workspace is paused. Two conditions rather than one list,
   * because they are two different states that happen to share a treatment — folding them together
   * is what made `signedOutOnly` mean two things once already.
   */
  if (current?.path === SUSPENDED_PATH) return <Navigate to={here(HOME_PATH)} replace />;

  if (current?.chrome === 'bare') {
    /**
     * ⚠️⚠️ **THROUGH A `<Route>`, FOR EXACTLY THE REASON THE `signedOutOk` BRANCH ABOVE RECORDS —
     * AND THIS BRANCH HAD THE BUG.** `useParams()` reads the router's MATCH, so an element rendered
     * as a bare fragment gets an empty params object. It went unnoticed while `/invite/:token` was
     * the only bare route, because that one is `signedOutOk` and never reaches here; C32's
     * `/marketplace/:extensionId` is bare and signed-in, and every extension page opened onto
     * *"no such extension"* — the screen read `extensionId` as `undefined` and asked the catalogue
     * for nothing.
     *
     * ⚠️ **And the route test could not see it**, for the same reason it could not see the invite
     * version: the not-found body carries the same `data-screen` marker, because it is the same
     * page. The case that catches it has to assert the CONTENT the parameter produces.
     */
    return (
      <Routes>
        <Route path={current.path} element={current.element} />
      </Routes>
    );
  }

  /**
   * ⚠️⚠️ **IT APPLIES THE WORKSPACE PREFIX, AND IT DID NOT AT FIRST — WHICH A BROWSER FOUND AND NO
   * GATE DID.** Every `go(...)` here takes a workspace-relative path, exactly as the screens'
   * `useWorkspaceNavigate` does. The first version passed the path through untouched, so the two
   * sidebar sections built from DATA rather than from `ROUTES` — the starred projects and the saved
   * reports — rendered `href="/projects/<uuid>"`. Those links still *worked*, because
   * `resolveWorkspaceAddress` rescues a pre-R-38 address, so the only visible symptom was one
   * redirect per click. The reachability gate could not see it either: it checks the rows generated
   * from `ROUTES`, and these two sections are generated from the sidebar response.
   */
  const go = (path: string) => () => {
    void navigate(here(path));
  };

  const workspace = session.workspaces.find((entry) => entry.id === session.workspaceId);

  /**
   * 28 Oct 2026 · the reader's own logging issues on the *Idő* row, over the last thirty days: hours
   * on an unknown line item as a warning, short days as a note — the Logolás grid's two marks.
   */
  const unknownHours = sidebarHandle?.myLogging?.unknownHours ?? null;
  const shortDays = sidebarHandle?.myLogging?.shortDays ?? 0;
  const timeNotices: NavNotice[] = [
    ...(unknownHours === null
      ? []
      : [
          {
            tone: 'warning' as const,
            label: t('nav.time.unknownHours', { hours: fmtDuration(unknownHours) }),
          },
        ]),
    ...(shortDays === 0
      ? []
      : [{ tone: 'note' as const, label: t('nav.time.shortDays', { count: shortDays }) }]),
  ];

  /* The rows, built where the setup flow's preview builds them — `sidebar-rows.ts`. */
  const rows = sidebarRows({
    t,
    view: sidebarView,
    pathname: location.pathname,
    here,
    go,
    may: (action) => permissionSet.has(action),
    notices: { '/time': timeNotices },
  });

  /* The settings panel's rows, as real links — `workspaceSettings` above says what they are. */
  const settingsRows = (entries: readonly SettingsEntry[]): NavItem[] =>
    entries.map((entry) => ({
      label: entry.label,
      icon: entry.icon,
      href: here(entry.path),
      active: entry.active,
      onSelect: go(entry.path),
      ...(entry.marker === undefined ? {} : { marker: entry.marker }),
      ...(entry.warning === undefined ? {} : { warning: entry.warning }),
    }));

  /* The back arrow's address — `settingsOrigin` above. */
  const workspaceRoot = here(HOME_PATH).slice(0, -HOME_PATH.length);
  const origin = settingsOrigin.current;
  const backTo = origin.startsWith(`${workspaceRoot}/`) ? origin : here(HOME_PATH);
  const workspaceIcon =
    workspace?.hasIcon === true ? workspaceIcons.get(iconPathOf(workspace.id)) : undefined;
  const userName = session.displayName ?? session.email ?? '';
  const userInitials = shellInitials(session.displayName, session.email);

  return (
    <AppShell
      labels={{
        navigation: t('nav.main'),
        search: t('nav.search'),
        searchHint: '⌘K',
        notifications: t('nav.notifications'),
        unreadCount: (count) => t('nav.unread', { count }),
        markAllRead: t('nav.markAllRead'),
        noNotifications: t('nav.noNotifications'),
        workspaceMenu: t('nav.workspaceMenu'),
        userMenu: t('nav.userMenu'),
        skipToContent: t('nav.skipToContent'),
        collapseSidebar: t('nav.collapseSidebar'),
        expandSidebar: t('nav.expandSidebar'),
        seeAll: t('nav.seeAll'),
      }}
      /* ⚠️ The person's OWN preference, in settings too: settings mode narrows the rail without
         touching it, so leaving settings puts back the width they chose (`app-shell.tsx`). */
      collapsed={sidebarCollapsed}
      {...(settingsMode === null
        ? {}
        : {
            /**
             * ┌──────────────────────────────────────────────────────────────────┐
             * │ ⚠️⚠️ THE SETTINGS PANEL — 14 Oct 2026, after Shopify's admin.              │
             * └──────────────────────────────────────────────────────────────────┘
             *
             * ⚠️⚠️ **ONE KIND OF SETTINGS AT A TIME.** The workspace menu opens the workspace's
             * settings and the person's menu opens their own; the panel shows the one that was
             * opened, never both (the owner, the same day). The rail keeps no settings gear —
             * the two menus are the ways in.
             */
            settings: {
              title: settingsMode === 'account' ? t('nav.account') : t('nav.settings'),
              back: {
                label: t('common.back'),
                href: backTo,
                onSelect: () => void navigate(backTo),
              },
              /* ⚠️ 17 Oct 2026 · the main search's button: it opens the palette, scoped to the
                 settings above — sections, their headings and what they hold. */
              search: {
                placeholder: t('nav.settingsSearch'),
                hint: '⌘K',
                onOpen: () => {
                  setPaletteOpen(true);
                },
              },
              /* ⚠️ 17 Oct 2026 · no name, e-mail or workspace block above the rows, and no person at
                 the foot — the product owner removed both; the rail and its menus already say who
                 and where. */
              groups: [
                settingsMode === 'account'
                  ? { id: 'account', items: settingsRows(accountSettings) }
                  : { id: 'workspace', items: settingsRows(workspaceSettings) },
              ],
            },
          })}
      onCollapsedChange={(next) => {
        setSidebarCollapsed(next);
        try {
          localStorage.setItem(SIDEBAR_KEY, next ? '1' : '0');
        } catch {
          /* Not remembered; the toggle still works for this visit. */
        }
      }}
      workspace={{
        /**
         * ⚠️ C42 · **the NAME at last.** This read the slug for twenty chunks and said so: *"a
         * workspace has no name on the wire, so inventing one here would be inventing data — it
         * gains a name when the workspace read does."* `0062` made that read.
         */
        name: workspace?.name ?? t('nav.noWorkspace'),
        initial: (workspace?.name ?? '?').slice(0, 1).toUpperCase(),
        iconSrc: workspaceIcon,
        /* 22 Oct 2026 · no heading over nothing — a reader without settings, other workspaces or
           `workspace.create` has an empty menu, and a lone *WORKSPACE* reads as a broken one. */
        menu: headed(t('nav.workspaceMenu'), [
          /* ⚠️ **`'/settings'`, not `'/settings/'`.** C16 folded the metafields and permissions
             routes into the settings rail, so no sidebar route matches the trailing slash any more —
             this menu quietly became a lone header. One `/settings` entry is what it now offers, and
             the rail takes it from there.

             ⚠️⚠️ **AND IT READS `ROUTES`, NOT `sidebar` — WHICH IS THE SAME BUG A SECOND TIME.**
             `sidebar` is `entrance === 'sidebar'`, so the moment Settings left the sidebar (it has
             an entrance here, and two entrances to one screen is what the mockup does not draw)
             this filter would have matched nothing and the menu would again be a lone header. The
             menu is the entrance; it cannot be derived from the thing it replaced. */
          /**
           * C42 · ⚠️⚠️ **THE OTHER WORKSPACES, AND ONLY WHEN THERE ARE OTHERS.** A switcher listing
           * one workspace is a menu that says the thing its own trigger already says — and it
           * teaches somebody that the list is where they are rather than where they could go. The
           * product owner's wording is exact: *"if there are two workspaces present in the tenant"*.
           */
          ...(session.workspaces.length < 2
            ? []
            : [
                ...session.workspaces.map((entry) => ({
                  label: entry.name,
                  /* ⚠️ A tick on the current one rather than a disabled row: disabling it would say
                     "you cannot go here", where the truth is "you are here". */
                  icon: entry.id === session.workspaceId ? ('check' as const) : undefined,
                  onSelect: () => {
                    if (entry.id === session.workspaceId) return;
                    /**
                     * ⚠️⚠️ **HOME, NOT WHEREVER THEY WERE.** Every id in this product is
                     * workspace-scoped, so `/tasks/HSO-1` in the workspace being left is a 404 in
                     * the one being entered — and a switcher that lands on a not-found is one
                     * nobody trusts. The same argument the sign-in redirect makes.
                     *
                     * ⚠️⚠️ **R-38 · A NAVIGATION, AND THE `selectWorkspace` CALL IS GONE FROM
                     * HERE.** It read `selectWorkspace(entry.id)` and then navigated, which is now
                     * two sources for one fact: `WorkspaceScope` adopts whatever workspace the
                     * address names, so the address IS the switch. Keeping both would mean the
                     * session moving a render before the URL did — and every screen's mount read
                     * going out with the new header while the old screen was still on the page.
                     */
                    void navigate(workspacePath(entry.slug, HOME_PATH));
                  },
                })),
                { type: 'separator' as const },
              ]),

          /* ⚠️ R-38 · `scoped('/settings')` is what the table holds now, so the filter compares
             against the pattern and the click navigates to the concrete address. */
          /* 22 Oct 2026 · only for somebody who may manage the workspace's settings — it opened an
             empty, refused panel for everybody else. */
          ...ROUTES.filter(
            (route) => route.path === scoped('/settings') && permissionSet.has('settings.manage'),
          ).map((route) => ({
            label: route.navLabel === undefined ? t(route.title) : t(route.navLabel),
            icon: route.icon,
            onSelect: go('/settings'),
          })),

          /* C42 · ⚠️ Under Settings, which is where the product owner put it. 22 Oct 2026 · only
             for a holder of `workspace.create` (`0236`) — OWNER and ADMIN by default. */
          ...(permissionSet.has('workspace.create')
            ? [
                {
                  label: t('workspaces.create'),
                  icon: 'plus' as const,
                  onSelect: () => {
                    setCreatingWorkspace(true);
                  },
                },
              ]
            : []),
        ]),
      }}
      user={{
        /* ⚠️ C22 · the DISPLAY NAME when the identity has one. This chip rendered the email
           twice — once as the name — and cut its initials from the address. */
        name: userName,
        email: session.email ?? '',
        initials: userInitials,
        /* ⚠️ C68 · the chip is the ONE animated blobatar — `app-shell.tsx` says why. The
           seed is the address, normalised here by the same function the sign-in card runs
           over what somebody typed, so the face they meet is the face they were shown. */
        avatarSeed: avatarSeed(session.email ?? ''),
        avatarSrc: avatar,
        ...(trackerMissing ? { warning: t('trackerConnection.missing') } : {}),
        menu: [
          {
            label: t('nav.account'),
            icon: 'user-round',
            onSelect: go('/account'),
            /* 21 Oct 2026 · the same warning as the chip's, on the way to where it is fixed. */
            ...(trackerMissing ? { warning: t('trackerConnection.missing') } : {}),
          },
          /* F-1 · the tour again, any time — the welcome card's promise. */
          {
            label: t('nav.takeTour'),
            icon: 'play',
            onSelect: () => {
              setTouring(true);
            },
          },
          /* 3 Nov 2026 · DU-1 · hours or man-days, everywhere at once. A switch, and the menu stays
             open so the pill that moved is the confirmation. */
          {
            label: t('nav.durationDays'),
            icon: 'calendar-days',
            toggle: true,
            checked: durationUnit === 'days',
            onSelect: () => {
              saveDurationUnit(durationUnit === 'days' ? 'hours' : 'days');
            },
          },
          { type: 'separator' },
          /* ⚠️ Sign-out lives in the chrome, not on the devices screen. "Sign out" there would mean
             revoking the session you are using, which is a different act from revoking a laptop's. */
          {
            label: t('session.signOut'),
            icon: 'log-out',
            destructive: true,
            onSelect: () => {
              void session.signOut();
            },
          },
        ],
      }}
      /**
       * ┌──────────────────────────────────────────────────────────────────┐
       * │ ⚠️⚠️ KÉRELMEK IS A ROW THAT OPENS, IN THE MAIN LIST — 20 Sep.                │
       * └──────────────────────────────────────────────────────────────────┘
       *
       * It was a SECTION in the scroll region, which put a heading above two rows and moved them
       * out of the sequence they belong to. The product owner's shape: one *Kérelmek* row sitting
       * between Időrögzítés and Agent that opens to reveal the two personal screens.
       *
       * ⚠️⚠️ **THE ROW IS EMITTED WHERE THE FIRST REQUEST ROUTE IS DECLARED**, so the sequence is
       * the route table's rather than this function's. `routes.tsx` puts `/requests` and
       * `/to-decide` between `/time` and `/agent` for exactly that reason — and moving them there
       * later means moving them in ONE file.
       *
       * ⚠️ **One entitled screen stays a plain row.** *"Megfelelő jogosultság esetén két
       * menüpontra kell bontani"* — the split is what the entitlement buys, and a dropdown holding
       * one thing is a control that costs a click to say nothing.
       */
      nav={rows.nav}
      sections={rows.sections}
      /* C17 filled these in — the list was `[]` with a comment saying so until the centre existed.
         `AppShell` still renders `labels.noNotifications` when there is genuinely nothing. */
      notifications={bell.notifications}
      {...(bell.unreadCount === undefined ? {} : { unreadCount: bell.unreadCount })}
      onMarkAllRead={bell.onMarkAllRead}
      /* ⚠️ Item 71 · it opens the palette now. The paragraph that stood here, explaining why it
         went to the task list instead, is on `useCommandPalette` above. */
      onOpenSearch={() => {
        setPaletteOpen(true);
      }}
      /**
       * ⚠⚠️ **WHATEVER THE SCREEN PUT THERE, FALLING BACK TO THE ROUTE'S TITLE.** The mockup's
       * header branches seven ways — a dropdown on the time section, a back button and a status pill
       * on a project, an editable name on the report builder — and this used to render one static
       * `<h1>` for all of them. `page-header.tsx` is the seam; a screen that claims nothing still
       * gets its title, which is every list screen.
       *
       * ⚠️ **`text-[13.5px]`, not `13px`** — the mockup's own size for this bar, and the reason it
       * read as a line of body text rather than as a heading.
       */
      /* ⚠️ `heading` first — see `routes.tsx`. The tab keeps the contextual title; this bar shows
         the AREA, so a settings section does not name itself twice. */
      header={
        pageHeader ?? <PlainTitle>{t(current?.heading ?? current?.title ?? 'app.name')}</PlainTitle>
      }
      /* F-5 · while the workspace still holds demo data, on every page and for everybody. */
      banner={sidebarView.demoData ? <DemoBanner /> : undefined}
    >
      {/* F-1 · the first time this person signs in, anywhere. Portalled, so where it sits in the
          tree is only about having the shell's providers above it. */}
      <WelcomeCards
        open={onboarding.state !== null && !onboarding.state.welcomeSeen}
        demo={sidebarView.demoData}
        canTour
        onDone={(choice) => {
          onboarding.record('welcome_seen');
          if (choice === 'skip') onboarding.record('tour_skipped');
          if (choice === 'tour') setTouring(true);
        }}
      />
      {touring && tourPermissions.loaded ? (
        <Tour
          context={{
            has: tourPermissions.has,
            scopeOf: tourPermissions.scopeOf,
            entitlements: sidebarView.entitlements,
            features: sidebarView.features,
            demo: sidebarView.demoData,
          }}
          memberId={workspace?.memberId ?? null}
          navigate={(path) => {
            go(path)();
          }}
          onEnd={(how) => {
            onboarding.record(how === 'closed' ? 'tour_skipped' : 'tour_finished');
            setTouring(false);
          }}
        />
      ) : null}
      <Routes>
        {/* ⚠️ `replace`, so the redirect does not put an entry in the history that sends Back
            straight forward again.

            ⚠️⚠️ **R-38 · `/:workspace`, NOT `/`.** Bare `/` never reaches this router: `Gate`
            renders `WorkspaceScope` first, and `resolveWorkspaceAddress` redirects `/` to
            `/{workspace}/home` because it is the thing that knows which workspace. What DOES
            reach here is `/{workspace}` on its own — somebody trimming an address back to the
            workspace, or the switcher's own target before `HOME_PATH` is appended — and without
            this row that lands on the catch-all's 404 inside a working shell. */}
        <Route path={scoped('/')} element={<Navigate to={here(HOME_PATH)} replace />} />
        {ROUTES.filter((route) => route.chrome === 'shell').map((route) => (
          <Route
            key={route.path}
            path={route.path}
            element={
              /* 26 Oct 2026 · ⚠️ **AN ENTITLEMENT CLOSES THE ADDRESS TOO, NOT ONLY THE ROW.** The
                 sidebar hid *Idő* from somebody who does not log time, and typing `/time` still
                 opened it. A route that names an entitlement the server answered `false` draws
                 the not-available state instead of its screen. Before the sidebar read lands
                 the entitlements are `true`, so nothing flashes. */
              route.entitlement !== undefined && !sidebarView.entitlements[route.entitlement] ? (
                <NotAvailable />
              ) : /* 3 Nov 2026 · ⚠️ **and an address that needs an action draws the denied
                   state without it** — `RouteEntry.requires`. Until the permission set lands the
                   screen renders as it always did; the server refuses either way. Inside the
                   settings the denied body keeps the settings frame, as the sections drew it. */
              route.requires !== undefined &&
                permissionSet.loaded &&
                !permissionSet.has(route.requires) ? (
                settingsMode === 'settings' ? (
                  <SettingsNotAvailable section={settingsSectionOf(route.path)} />
                ) : (
                  <NotAvailable />
                )
              ) : (
                /* 3 Nov 2026 · ⚠️ **keyed on the record the address names** — `route-key.tsx`.
                   The `key` above is the list's, and React Router never hands it to the element. */
                <KeyedByRecord route={route}>{route.element}</KeyedByRecord>
              )
            }
          />
        ))}
        {/* ⚠️ **The catch-all is LAST and it is explicit.** *"A route silently fell through to
            the catch-all"* is one of the four failures §4 names — a wildcard that renders a
            plausible screen makes a typo'd path look like a working one. This renders something
            that cannot be mistaken for a screen. */}
        <Route path="*" element={<NotFound />} />
      </Routes>

      {/* C42 · ⚠️ **Inside the shell, because the menu that opens it is.** A dialog mounted beside
          the router would close on every navigation the switcher performs. */}
      <WorkspaceDialog
        open={creatingWorkspace}
        onClose={() => {
          setCreatingWorkspace(false);
        }}
        onCreated={(created) => {
          setCreatingWorkspace(false);
          /**
           * ⚠️⚠️ **REFRESH FIRST, THEN SWITCH.** `selectWorkspace` only sets an id; the workspace
           * itself is not in `session.workspaces` until `/auth/me` is read again, and switching to
           * an id the session does not know would leave the shell drawing "no workspace" while
           * every request carried a header for a workspace the switcher could not name.
           */
          /**
           * ⚠️⚠️ **R-38 · THE RELOAD IS STILL FIRST, FOR A SHARPER REASON THAN BEFORE.** It used to
           * be that `selectWorkspace` sets only an id and the workspace is not in
           * `session.workspaces` until `/auth/me` is read again. Now the NAVIGATION is the switch,
           * and `resolveWorkspaceAddress` answers `unknown` for a slug that is not in the
           * membership list — so navigating before the reload lands the person who just created a
           * workspace on *"that address does not lead anywhere"*, naming the workspace they are
           * looking at in the dialog.
           *
           * ⚠️ **And `selectWorkspace` is gone from here**: the address adopts the workspace, so
           * calling it as well would move the session a render before the URL did.
           */
          void session.reload().then(() => {
            void navigate(workspacePath(created.slug, HOME_PATH));
          });
        }}
      />

      {/**
       * ⚠️⚠️ **INSIDE THE SHELL, BECAUSE IT IS A WORKSPACE'S PALETTE — item 71.** It searches this
       * workspace's records and navigates within it; rendering it outside would put it on the
       * signed-out screens too, where every one of its rows is a link that cannot work.
       *
       * ⚠️ **`CommandDialog` portals to the body regardless**, so being nested here costs no
       * stacking context — `overlay.ts` owns that, and it is the same arrangement every dialog in
       * the product already has.
       */}
      <CommandDialog
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        onQueryChange={palette.onQueryChange}
        items={palette.items}
        placeholder={t('palette.placeholder')}
        label={t('nav.search')}
        emptyLabel={(query) => t('palette.empty', { query })}
        loading={palette.loading}
        loadingLabel={t('palette.searching')}
      />
    </AppShell>
  );
}

/** 26 Oct 2026 · a screen this person's entitlements close — the settled *denied* state. */
function NotAvailable(): ReactNode {
  return (
    /* A screen marker of its own, so the route checks can tell it from the page it stands in for. */
    <div data-screen="not-available" className="contents">
      <ScreenState failure={{ kind: 'denied' }} onRetry={() => undefined}>
        {null}
      </ScreenState>
    </div>
  );
}

/**
 * 3 Nov 2026 · the same state inside the workspace settings: the mockup's *"Nincs jogosultságod"*
 * body in the settings frame, which is what `/settings`, `/settings/data` and `/settings/billing`
 * already drew for themselves.
 */
function SettingsNotAvailable({ section }: { readonly section: string }): ReactNode {
  return (
    <div data-screen="not-available" className="contents">
      <Framed section={section}>
        <SettingsDenied />
      </Framed>
    </div>
  );
}

/** The section key of a settings route's pattern — `/settings` is `general`. */
function settingsSectionOf(path: string): string {
  return withoutWorkspacePrefix(path).split('/')[2] ?? 'general';
}

function NotFound(): ReactNode {
  const location = useLocation();
  return (
    <div data-screen="not-found" className="p-8">
      <p className="text-[13px] font-medium">404</p>
      {/* The path is not translated: it is the address the browser has, verbatim, in mono. */}
      <p className="text-muted-foreground mt-1 text-[12px]">{location.pathname}</p>
    </div>
  );
}

/** A menu's heading, and the items under it — or nothing at all when there are none. */
function headed<T>(label: string, items: readonly T[]): (T | { type: 'label'; label: string })[] {
  return items.length === 0 ? [] : [{ type: 'label', label }, ...items];
}
